import { FC, PropsWithChildren } from "react";
import styles from "./styles.module.css";
import Stack from "@/components/atoms/Stack";
import CTA from "@/components/atoms/CTA";
import Group from "@/components/atoms/Group";
import { useTranslation } from "react-i18next";
import { ContractStatus, Order } from "@/types";
import Heading from "@/components/atoms/Heading";
import Spinner from "@/components/atoms/Spinner/Spinner";
import { useAuth } from "@/contexts/AuthContext";

interface TradeHeaderProps {
  orders: Order[];
  createNew: () => void;
  companyName: string;
}

interface ContractsHeaderProps {
  companyName: string;
  sortOptions: any[];
  totalContracts: number;
  isFetchingContracts: boolean;
  onClickNewContract: () => void;
  status: ContractStatus;
}

interface SubHeaderProps {
  variant?: "primary" | "secondary";
}

export const SubHeaderBase: FC<PropsWithChildren<SubHeaderProps>> = ({ variant = "primary", ...props }) => {
  const rootClass = `${styles.subHeaderRoot} ${styles[variant]}`;
  return (
    <header className={rootClass}>
      <div className={styles.inner}>{props.children}</div>
    </header>
  );
};

export const TradeHeader: FC<TradeHeaderProps> = (props) => {
  const { orders, createNew, companyName } = props;
  const { t } = useTranslation();

  const { currentCompany, isViewer } = useAuth();

  return (
    <SubHeaderBase>
      <Stack gap="4px">
        <Heading order={2} styleOrder={5} tt="uppercase" strong={true}>
          {companyName}
        </Heading>
        <Heading order={3} styleOrder={6} tt="uppercase">
          {orders.length} {t("orders:activeOrders")}
        </Heading>
      </Stack>
      <CTA size="lg" intent="primary" disabled={isViewer(currentCompany)} onClick={createNew}>
        {t("orders:newOrder")}
      </CTA>
    </SubHeaderBase>
  );
};

export const ContractsHeader: FC<ContractsHeaderProps> = (props) => {
  const { companyName, totalContracts, onClickNewContract } = props;
  const { t } = useTranslation();

  return (
    <SubHeaderBase variant="secondary">
      <Group flex={1} justify="space-between" gap="20px">
        <Group justify="normal">
          <Stack gap="4px">
            <Heading order={2} styleOrder={5} tt="uppercase" strong={true}>
              {companyName}
            </Heading>
            <Heading order={3} styleOrder={6} tt="uppercase">
              {totalContracts}{" "}
              {props.status ? t(`enumerables:contractStatusPlural.${props.status}`) : t("contracts:totalContracts")}
            </Heading>
          </Stack>
          <Spinner inline loading={props.isFetchingContracts} />
        </Group>
        <CTA onClick={onClickNewContract} data-testid={"new-contract-btn"}>
          {t("contracts:newContract")}
        </CTA>
      </Group>
    </SubHeaderBase>
  );
};
